import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, TemplateRef } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { AsLinkDirective } from '@directives/as-link/as-link.directive';
import { StopClickPropagationDirective } from '@directives/stop-click-propagation/stop-click-propagation.directive';
import { DeviceAsset, LineAsset, UserAsset, UserAssetData } from '@models/dashboard.model';
import {
  MULTI_SIM_TYPES,
  calculateMultiSimType,
  constructAssetTitle,
  fieldValueIsPresent,
  hasDeviceId,
  hasLineId,
  hasMultiSim
} from '@models/functions.model';
import {
  MyAssetsStatusFilter,
  MyAssetsStatusFormControls,
  MyAssetsStatusFormGroup
} from '@models/my-assets.model';
import { NgbAccordionModule, NgbDropdownModule, NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { TranslationPipe } from '@pipes/translation/translation.pipe';
import { DashboardService } from '@services/dashboard/dashboard.service';
import { ModalService } from '@services/modal/modal.service';
import { MyAssetsFilterStateService } from '@services/my-assets-filter-state/my-assets-filter-state.service';
import { TranslationService } from '@services/translation/translation.service';
import { DashboardAssetComponent } from '@shared-components/asset-types/dashboard-asset/dashboard-asset.component';
import { MultisimOffcanvasComponent } from '@shared-components/multisim-offcanvas/multisim-offcanvas.component';
import { Subscription } from 'rxjs';
import { DataLoaderDirective } from 'tangoe-design-system';

@Component({
  selector: 'ts-my-assets',
  templateUrl: './my-assets.component.html',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslationPipe,
    DashboardAssetComponent,
    MultisimOffcanvasComponent,
    AsLinkDirective,
    DataLoaderDirective,
    NgbAccordionModule,
    NgbDropdownModule,
    StopClickPropagationDirective
  ]
})
export class MyAssetsComponent implements OnInit, OnDestroy {
  my_assets: UserAsset[] = [];
  my_assets_ghost: UserAsset[] = [];
  primary_asset: UserAsset | undefined;
  asset_status_filters: MyAssetsStatusFormGroup = new FormGroup<MyAssetsStatusFormControls>(
    {} as MyAssetsStatusFormControls
  );
  asset_status_filters_count: number;
  offcanvas_asset: UserAsset | undefined;
  multi_sim_types: string[] = MULTI_SIM_TYPES;
  private subscriptions: Subscription = new Subscription();

  constructor(
    private dashboard_svc: DashboardService,
    private filter_state_svc: MyAssetsFilterStateService,
    private modal_svc: ModalService,
    private offcanvas_svc: NgbOffcanvas,
    private translation_svc: TranslationService
  ) {
    for (const [key, value] of Object.entries(this.filter_state_svc.getStatusFilter())) {
      this.asset_status_filters.addControl(
        key as keyof MyAssetsStatusFormControls,
        new FormControl(value),
        { emitEvent: false }
      );
    }
  }

  ngOnInit(): void {
    this.loadAssetsList();
    this.getFilterCount(this.asset_status_filters.value);

    this.subscriptions.add(
      this.asset_status_filters.valueChanges.subscribe((values) => {
        this.getFilterCount(values);
        this.filter_state_svc.setStatusFilter(values);
      })
    );

    this.subscriptions.add(
      this.filter_state_svc.getStatusFilterSubject().subscribe((filters: MyAssetsStatusFilter) => {
        this.asset_status_filters.setValue(filters, { emitEvent: false });
        this.filterAssets(filters);
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  hasLineId(line: LineAsset): boolean {
    return hasLineId(line);
  }

  hasDeviceId(device: DeviceAsset): boolean {
    return hasDeviceId(device);
  }

  hasMultiSim(asset: UserAsset): boolean {
    return hasMultiSim(asset);
  }

  confirmPrimaryLine(asset: UserAsset) {
    const primary_filter_asset: UserAsset[] = this.checkForPrimary();

    this.modal_svc.confirmationDialog(
      {
        title: this.translation_svc.translate('set_primary_contact'),
        message:
          primary_filter_asset.length > 0
            ? this.translation_svc.translate('primary_line_change_from', [
                `<span class='fw-medium text-nowrap'>${primary_filter_asset[0].line.international_number}</span>`,
                `<span class='fw-medium text-nowrap'>${asset.line.international_number}</span>`
              ])
            : this.translation_svc.translate(
                'primary_line_change_to',
                `<span class='fw-medium text-nowrap'>${asset.line.international_number}</span>`
              ),
        confirm_button_text: this.translation_svc.translate('set_primary'),
        cancel_button_text: this.translation_svc.translate('cancel'),
        modal_data: asset,
        onConfirm: (data) => {
          this.dashboard_svc.updatePrimaryLine(data.line.id).subscribe(() => this.loadAssetsList());
        }
      },
      {
        size: 'dialog-centered',
        backdrop: 'static'
      }
    );
  }

  isHiddenPrimaryOption(asset: UserAsset): boolean {
    let is_hide_primary_option: boolean;
    const current_status: string = (asset.line?.status).toLowerCase() as string;
    const is_primary_contact: boolean = asset.line?.is_primary_contact as boolean;
    is_hide_primary_option = is_primary_contact || current_status === 'inactive' ? true : false;
    return is_hide_primary_option;
  }

  assetAriaLabel(asset: UserAsset): string {
    return constructAssetTitle(asset);
  }

  openAssetOffCanvas(content: TemplateRef<any>, asset: UserAsset): void {
    this.offcanvas_asset = asset;

    this.offcanvas_svc.open(content, {
      position: 'end',
      panelClass: 'multisim-offcanvas',
      backdrop: 'static'
    });
  }

  closeOffCanvas(): void {
    this.offcanvas_asset = undefined;
  }

  fieldExists(field: string | undefined): string | undefined {
    return fieldValueIsPresent(field);
  }

  private loadAssetsList(): void {
    this.dashboard_svc.userAssets({ index: true }).subscribe({
      next: (data: UserAssetData) => {
        this.my_assets_ghost = data.assets as UserAsset[];

        this.my_assets_ghost.forEach((asset: UserAsset) => {
          asset.multi_sim_type = calculateMultiSimType(asset);
        });

        this.filterAssets(this.asset_status_filters.value);
      }
    });
  }

  private filterAssets(filters: Partial<MyAssetsStatusFilter>): void {
    if (!this.my_assets_ghost) return;

    this.my_assets = this.my_assets_ghost.filter((asset) =>
      Boolean(
        filters[
          this.hasLineId(asset.line)
            ? (asset.line.status.toLowerCase() as keyof MyAssetsStatusFilter)
            : 'device_no_line'
        ]
      )
    );
  }

  private checkForPrimary(): UserAsset[] {
    const is_primary_contact: UserAsset[] = this.my_assets.filter(
      (item: UserAsset) => item.line.is_primary_contact
    );
    return is_primary_contact;
  }

  private getFilterCount(filters: Partial<MyAssetsStatusFilter>): void {
    this.asset_status_filters_count = Object.values(filters).reduce((acc, value) => {
      if (value) acc++;
      return acc;
    }, 0);
  }
}
